import { z } from 'zod';
import {
  MAX_CHECKLIST_ITEMS,
  ChecklistItemSchema,
  checklistProgress,
  type ChecklistItem,
  type Todo,
} from '../../domain/entities/todo.ts';
import { newId } from '../../domain/id.ts';
import { expectUnchanged } from '../../ports/batch.ts';
import { NotFoundError } from '../../ports/repository.ts';
import { TodoError, type TodoContext } from './save-todo.ts';

/**
 * A to-do's checklist.
 *
 * Every function here writes the whole array, guarded with the version it
 * read. That is the point rather than a shortcut: a reorder, a tick and an
 * added row are all one field write, so two people working on the same
 * checklist get a detected conflict instead of a list that silently lost a
 * row. A subcollection would make each row independently writable and each
 * read N reads, for a list that is always read with its to-do anyway.
 */
/**
 * Re-exported, not redefined. The cap is a property of the entity and
 * lives beside it now that `checklistTemplates` caps itself at the same
 * number; this keeps every existing importer working.
 */
export { MAX_CHECKLIST_ITEMS };

export const ChecklistTextSchema = z.string().trim().min(1, 'A checklist row needs text').max(500);

async function load(context: TodoContext, todoId: string): Promise<Todo> {
  const existing = await context.data
    .collection<Todo>({ companyId: context.companyId, jobId: context.jobId }, 'todos')
    .get(todoId);
  if (!existing) throw new NotFoundError('todos', todoId);
  return existing;
}

async function writeChecklist(
  context: TodoContext,
  existing: Todo,
  checklist: readonly ChecklistItem[],
): Promise<Todo> {
  const now = context.clock.now().toISOString();
  const patch = { checklist: [...checklist], updatedAt: now, updatedBy: context.uid };
  await context.data.commit([
    {
      kind: 'update',
      scope: { companyId: context.companyId, jobId: context.jobId },
      collection: 'todos',
      id: existing.id,
      patch,
      expect: expectUnchanged(existing),
    },
  ]);
  return { ...existing, ...patch };
}

export async function addChecklistItem(
  context: TodoContext,
  todoId: string,
  text: string,
  assigneeIds: readonly string[] = [],
): Promise<Todo> {
  const parsed = ChecklistTextSchema.safeParse(text);
  if (!parsed.success) throw new TodoError('invalid', parsed.error.issues[0]!.message);

  const existing = await load(context, todoId);
  if (existing.checklist.length >= MAX_CHECKLIST_ITEMS) {
    // A bound, because the checklist is one document field and an unbounded
    // array is an unbounded document — Firestore refuses the write at a
    // megabyte with nothing useful to say.
    throw new TodoError(
      'invalid',
      `A checklist holds at most ${MAX_CHECKLIST_ITEMS} rows. Split the to-do.`,
    );
  }

  const item: ChecklistItem = {
    id: newId(),
    text: parsed.data,
    done: false,
    assigneeIds: [...new Set(assigneeIds)],
  };
  const checked = ChecklistItemSchema.safeParse(item);
  if (!checked.success) throw new TodoError('invalid', checked.error.issues[0]!.message);

  // Appended. New rows go at the bottom because that is where somebody
  // adding one is looking, and because inserting would renumber a list
  // other people are reading.
  return writeChecklist(context, existing, [...existing.checklist, item]);
}

export async function renameChecklistItem(
  context: TodoContext,
  todoId: string,
  itemId: string,
  text: string,
): Promise<Todo> {
  const parsed = ChecklistTextSchema.safeParse(text);
  if (!parsed.success) throw new TodoError('invalid', parsed.error.issues[0]!.message);

  const existing = await load(context, todoId);
  if (!existing.checklist.some((item) => item.id === itemId)) {
    throw new NotFoundError('todos', `${todoId}/checklist/${itemId}`);
  }

  return writeChecklist(
    context,
    existing,
    existing.checklist.map((item) => (item.id === itemId ? { ...item, text: parsed.data } : item)),
  );
}

export async function removeChecklistItem(
  context: TodoContext,
  todoId: string,
  itemId: string,
): Promise<Todo> {
  const existing = await load(context, todoId);
  return writeChecklist(
    context,
    existing,
    existing.checklist.filter((item) => item.id !== itemId),
  );
}

/**
 * Tick one row off, or put it back.
 *
 * Who and when are recorded per row, for the same reason they are on the
 * to-do: "the slab was checked" and "Mike checked it on Tuesday" answer
 * different questions. Ticking a ticked row keeps the first answer.
 */
export async function setChecklistItemDone(
  context: TodoContext,
  todoId: string,
  itemId: string,
  done: boolean,
): Promise<Todo> {
  const existing = await load(context, todoId);
  const row = existing.checklist.find((item) => item.id === itemId);
  if (!row) throw new NotFoundError('todos', `${todoId}/checklist/${itemId}`);
  if (row.done === done) return existing;

  const now = context.clock.now().toISOString();
  return writeChecklist(
    context,
    existing,
    existing.checklist.map((item) =>
      item.id === itemId
        ? done
          ? { ...item, done: true, doneAt: now, doneBy: context.uid }
          : { ...item, done: false, doneAt: undefined, doneBy: undefined }
        : item,
    ),
  );
}

/** Tick every row off in one write, which is what "Complete All" is. */
export async function completeChecklist(context: TodoContext, todoId: string): Promise<Todo> {
  const existing = await load(context, todoId);
  const outstanding = existing.checklist.some((item) => !item.done);
  if (!outstanding) return existing;

  const now = context.clock.now().toISOString();
  return writeChecklist(
    context,
    existing,
    existing.checklist.map((item) =>
      // Rows already done keep the time they were done. Re-stamping them
      // would rewrite a record of when work actually happened.
      item.done ? item : { ...item, done: true, doneAt: now, doneBy: context.uid },
    ),
  );
}

export async function removeChecklist(context: TodoContext, todoId: string): Promise<Todo> {
  const existing = await load(context, todoId);
  return writeChecklist(context, existing, []);
}

/**
 * Move a row to a new position.
 *
 * Pure, so a drag can be previewed before it is saved. An index outside the
 * list ends up at the nearest end rather than being refused — a drag that
 * finished past the bottom means "put it at the bottom", which is what the
 * person doing it meant.
 *
 * That clamping is `splice`'s own, not an explicit guard. An earlier version
 * clamped the index first; a mutation test showed the clamp could be removed
 * without changing a single result, including for negative indices, so it
 * was code no test could justify. The tests below pin the behaviour either
 * way, which is what makes relying on it safe.
 */
export function reorderChecklist(
  checklist: readonly ChecklistItem[],
  itemId: string,
  toIndex: number,
): ChecklistItem[] {
  const from = checklist.findIndex((item) => item.id === itemId);
  if (from === -1 || from === toIndex) return [...checklist];

  const next = [...checklist];
  const [moved] = next.splice(from, 1);
  next.splice(toIndex, 0, moved!);
  return next;
}

export async function saveChecklistOrder(
  context: TodoContext,
  todoId: string,
  itemId: string,
  toIndex: number,
): Promise<Todo> {
  const existing = await load(context, todoId);
  return writeChecklist(context, existing, reorderChecklist(existing.checklist, itemId, toIndex));
}

/** Rows this person is named on, for "what am I meant to be doing". */
export function checklistRowsFor(
  todo: Pick<Todo, 'checklist'>,
  uid: string,
): readonly ChecklistItem[] {
  return todo.checklist.filter((item) => item.assigneeIds.includes(uid));
}

export { checklistProgress };
